import api from "../api/api";

export interface User {
  id: string;
  name: string;
  clients: string;
  designation: string;
  username: string;
  role: string;
  status: string;
  plan: string;
  usage?: number;
  remaining_quota: number;
  last_login: string;
}

export interface CreateUserDTO {
  name: string;
  client_id: string;
  designation: string;
  username: string;
  password?: string;
  plan?: string;
  max_quota: number;

}

interface CreateUserResponse {
  message: string;
  user_id: number;
}

export interface BulkCreateUserDTO {
  users: Array<{
    name: string;
    username: string;
    designation?: string;
    password?: string;
  }>;
  client_id: string;
  default_designation: string;
  default_plan?: string;
  default_max_quota?: number;
  default_status?: string;
}

interface BulkCreateResponse {
  message: string;
  created: number;
  failed: number;
  failed_users?: Array<{username: string; reason: string}>;
  emails_sent: number;
}

export async function bulkCreateUsers(
  bulkData: BulkCreateUserDTO,
  access_token: string
): Promise<BulkCreateResponse> {
  try {
    const res = await api.post<BulkCreateResponse>("/api/v1/users/bulk-create", bulkData, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("Bulk users created:", res.data);
    return res.data;
  } catch (error: any) {
    console.error("Bulk create error:", error.response?.data || error.message);
    throw error;
  }
}

// Create new user
export async function createUser(
  userData: CreateUserDTO,
  access_token: string
): Promise<CreateUserResponse> {
  try {
    const res = await api.post<CreateUserResponse>("/api/v1/users/", userData, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("User created successfully:", res.data);

    return res.data;
  } catch (error: any) {
    console.error("Create user error:", error.response?.data || error.message);
    throw error;
  }
}

// Update user
export async function updateUser(
  userId: string,
  userData: Partial<CreateUserDTO>,
  access_token: string
): Promise<User> {
  try {
    const res = await api.put<User>(`/api/v1/users/${userId}`, userData, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("User updated successfully:", res.data);

    return res.data;
  } catch (error: any) {
    console.error("Update user error:", error.response?.data || error.message);
    throw error;
  }
}

// Update user quota with reports_this_month
export async function updateUserQuota(
  userId: string,
  quotaData: {
    max_quota: number;
    reports_this_month: number;
  },
  access_token: string
): Promise<{
  message: string;
  data: {
    user_id: string;
    max_quota: number;
    reports_this_month: number;
    new_remaining: number;
  }
}> {
  try {
    const res = await api.put(`/api/v1/users/${userId}/quota`, quotaData, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("User quota updated successfully:", res.data);
    return res.data;
  } catch (error: any) {
    console.error("Update user quota error:", error.response?.data || error.message);
    throw error;
  }
}

// Get single user
export async function getUser(
  userId: string,
  access_token: string
): Promise<User> {
  try {
    const res = await api.get<User>(`/api/v1/users/${userId}`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("Fetched user:", res.data);

    // Map backend fields into `User`
    const userData: any = res.data;
    return {
      id: userData.user_id ?? userData.id,
      name: userData.name,
      clients: userData.clients,
      designation: userData.designation,
      username: userData.username,
      role: userData.role ?? "user",
      status: userData.status,
      plan: userData.plan,
      usage: userData.usage,
      remaining_quota: userData.remaining_quota,
      last_login: userData.last_login
    };
  } catch (error: any) {
    console.error("Get user error:", error.response?.data || error.message);
    throw error;
  }
}

// Get all users
export async function getAllUsers(
  access_token: string
): Promise<User[]> {
  try {
    const res = await api.get<User[]>("/api/v1/users", {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("Fetched all users:", res.data);

    return res.data.map((user: any) => ({
      id: user.user_id ?? user.id,
      name: user.name,
      clients: user.clients,
      designation: user.designation,
      username: user.username,
      role: user.role ?? "user",
      status: user.status,
      plan: user.plan,
      usage: user.usage,
      remaining_quota: user.remaining_quota,
      last_login: user.last_login
    }));
  } catch (error: any) {
    console.error("Get all users error:", error.response?.data || error.message);
    throw error;
  }
}

// Delete user
export async function deleteUser(
  userId: string,
  access_token: string
): Promise<{ message: string }> {
  try {
    const res = await api.delete<{ message: string }>(`/api/v1/users/${userId}`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("User deleted successfully:", res.data);

    return res.data;
  } catch (error: any) {
    console.error("Delete user error:", error.response?.data || error.message);
    throw error;
  }
}

// Get current user profile
export async function getCurrentUserProfile(
  access_token: string
): Promise<User> {
  try {
    const res = await api.get<User>("/api/v1/users/profile/", {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("Fetched current user:", res.data);

    const userData: any = res.data;
    return {
      id: userData.user_id ?? userData.id,
      name: userData.name,
      clients: userData.clients,
      designation: userData.designation,
      username: userData.username,
      role: userData.role ?? "user",
      status: userData.status,
      plan: userData.plan,
      usage: userData.usage,
      remaining_quota: userData.remaining_quota,
      last_login: userData.last_login
    };
  } catch (error: any) {
    console.error("Get current user error:", error.response?.data || error.message);
    throw error;
  }
}

// Change password
export async function changePassword(
  oldPassword: string,
  newPassword: string,
  confirmPassword: string,
  access_token: string
): Promise<{ message: string }> {
  try {
    const res = await api.post<{ message: string }>("/api/v1/users/change-password/", {
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword
    }, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    console.log("Password changed successfully:", res.data);
    return res.data;
  } catch (error: any) {
    console.error("Change password error:", error.response?.data || error.message);
    throw error;
  }
}
